import React, { useCallback, useEffect, useMemo, useState } from "react";
import TabPosition from "../shared/TabPosition";
import { Helmet } from "react-helmet-async";
import { TranslationService } from "../../services/TranslationService";
import ConsolidatedService from "../../services/ConsolidatedService";
import CompanyService from "../../services/CompanyService";
import Loading from "../shared/components/Loading";
import { ToastService } from "../shared/bootstrap/Toast";
import { ReceivablesAgingGraphScale } from "../company/Configuration/ReceivablesAgingGraphScale";
import {
  ConsolidatedGetSettingsResponse,
  ConsolidatedSetSettingsRequest,
  ConsolidatedSettingsCompany
} from "../../entities/consolidated/ConsolidatedSettings";

const DEFAULT_SCALES =
  CompanyService.getCompanyAuth()?.GraphScales?.join(", ") ||
  "-90, -60, -30, 0, 30, 60, 90";

const ConsolidatedSettings: React.FC = () => {
  const { translate } = TranslationService;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(false);

  const [companies, setCompanies] = useState<ConsolidatedSettingsCompany[]>([]);
  const [scales, setScales] = useState<string>("");

  const sortedCompanies = useMemo(() => {
    return [...companies].sort((a, b) =>
      (a.CompanyName ?? "").localeCompare(b.CompanyName ?? "")
    );
  }, [companies]);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(false);
      const result = (await ConsolidatedService.getSettings()) as
        | Error
        | ConsolidatedGetSettingsResponse;
      if (result instanceof Error) {
        setError(true);
        setLoading(false);
        return;
      }

      setCompanies(Array.isArray(result.Companies) ? result.Companies : []);
      setScales(result.Scales || DEFAULT_SCALES);
      setLoading(false);
    };

    load();
  }, []);

  const setDefaultCurrency = (guid: string) => {
    setCompanies((prev) =>
      prev.map((c) => ({
        ...c,
        DefaultCurrency: c.CompanyGuid === guid
      }))
    );
  };

  const toggleConsolidate = (guid: string) => {
    setCompanies((prev) =>
      prev.map((c) =>
        c.CompanyGuid === guid
          ? { ...c, NoConsolidate: !(c.NoConsolidate ?? false) }
          : c
      )
    );
  };

  const handleScalesChange = useCallback((next: string) => setScales(next), []);

  const updateRate = (guid: string, next: number | null) => {
    setCompanies((prev) =>
      prev.map((c) =>
        c.CompanyGuid === guid ? { ...c, CurrencyRate: next } : c
      )
    );
  };

  const hasAtLeastOneConsolidated = companies.some(
    (c) => !(c.NoConsolidate ?? false)
  );

  const onSave = async () => {
    setSaving(true);
    try {
      const payload: ConsolidatedSetSettingsRequest = {
        Companies: companies,
        Scales: scales
      };
      const result = (await ConsolidatedService.setSettings(payload)) as
        | Error
        | unknown;
      if (result instanceof Error) {
        setError(true);
        ToastService.showToast(
          TranslationService.translate.ErrorProcessingRequest,
          undefined,
          "danger"
        );
      } else {
        ToastService.showToast(
          TranslationService.translate.SavedChanges,
          undefined,
          "success"
        );
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <TabPosition module="consolidated/settings">
      <Helmet>
        <title>
          Intiza - {translate.Consolidated} - {translate.Configuration}
        </title>
      </Helmet>

      <div className="container-fluid padding">
        <div className="main-panel configuration">
          <h2 className="mb-4">{translate.Configuration}</h2>

          {loading && <Loading height={300} />}

          {!loading && error && <div className="alert alert-danger">Error</div>}

          {!loading && !error && (
            <>
              <div className="card mb-4">
                <div className="card-body">
                  <h5 className="mb-3">{translate.Companies ?? "Empresas"}</h5>
                  <div className="table-responsive">
                    <table className="table">
                      <thead>
                        <tr>
                          <th>{translate.Company ?? "Empresa"}</th>
                          <th>{translate.Currencies ?? "Moneda"}</th>
                          <th>{translate.ExchangeRate ?? "Tipo de cambio"}</th>
                          <th>{"Moneda de referencia"}</th>
                          <th>{"¿Consolidar?"}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortedCompanies.map((c) => {
                          const consolidate = !(c.NoConsolidate ?? false);
                          return (
                            <tr key={c.CompanyGuid}>
                              <td>{c.CompanyName}</td>
                              <td>
                                {c.CurrencyName}
                                {c.CurrencySymbol &&
                                !c.CurrencyName?.includes(c.CurrencySymbol)
                                  ? ` ${c.CurrencySymbol}`
                                  : ""}
                              </td>
                              <td style={{ maxWidth: 160 }}>
                                <input
                                  className="form-control"
                                  value={c.CurrencyRate ?? ""}
                                  onChange={(e) => {
                                    const raw = e.target.value;
                                    if (raw === "")
                                      return updateRate(c.CompanyGuid, null);
                                    const n = Number(raw.replace(",", "."));
                                    updateRate(
                                      c.CompanyGuid,
                                      Number.isFinite(n) ? n : null
                                    );
                                  }}
                                />
                              </td>
                              <td>
                                <input
                                  type="radio"
                                  name="defaultCurrency"
                                  checked={Boolean(c.DefaultCurrency)}
                                  onChange={() =>
                                    setDefaultCurrency(c.CompanyGuid)
                                  }
                                />
                              </td>
                              <td>
                                <input
                                  type="checkbox"
                                  checked={consolidate}
                                  onChange={() =>
                                    toggleConsolidate(c.CompanyGuid)
                                  }
                                />
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              <div className="card mb-4">
                <div className="card-body">
                  <ReceivablesAgingGraphScale
                    initialValue={scales}
                    showSaveButton={false}
                    onChange={handleScalesChange}
                  />
                </div>
              </div>

              {!hasAtLeastOneConsolidated && (
                <div className="alert alert-warning mb-3">
                  {translate.MustConsolidateAtLeastOneCompany}
                </div>
              )}
              <div className="d-flex justify-content-end">
                <button
                  className="btn btn-primary"
                  onClick={onSave}
                  disabled={saving}
                >
                  {saving
                    ? (translate.Saving ?? "Guardando...")
                    : translate.Save}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </TabPosition>
  );
};

export default ConsolidatedSettings;
